import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/i18n";
import { usePageMeta, eur } from "@/lib/format";
import { PageHeader } from "@/components/StatCard";
import Seo from "@/components/Seo";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { ChevronLeft, Check, Crown, Sparkles } from "lucide-react";

const PLANS = ["coach_pro", "coach_pro_yearly"];
const FEATURE_KEYS = ["f_commission", "f_unlimited_weva", "f_branding", "f_analytics", "f_priority", "f_group_tools", "f_bulk_export"];

export default function Upgrade() {
  const { user, refreshUser } = useAuth();
  const t = useT();
  usePageMeta(t("upg.meta_title"));
  const [tiers, setTiers] = useState(null);
  const [mine, setMine] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try {
      const [{ data: ti }, { data: m }] = await Promise.all([
        api.get("/subscription/tiers"),
        api.get("/subscription/mine"),
      ]);
      setTiers(ti || {});
      setMine(m || {});
    } catch { setTiers({}); setMine({}); }
  }, []);
  useEffect(() => { if (user?.role === "coach") load(); }, [user, load]);

  if (user && user.role !== "coach") return <Navigate to="/app/circles" replace />;

  const currentTier = mine?.tier || user?.app_subscription?.tier || "free";
  const isPro = currentTier === "coach_pro" || currentTier === "coach_pro_yearly";

  const subscribe = async (tierKey, mode) => {
    setBusy(`${tierKey}-${mode}`);
    try {
      const { data } = await api.post("/subscribe/app", { tier: tierKey, payment_mode: mode, origin_url: window.location.href });
      if (data?.checkout_url) { window.location.href = data.checkout_url; return; }
      toast.success(t("upg.activated"));
      await refreshUser?.();
      await load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setBusy(null); }
  };

  return (
    <div data-testid="coach-upgrade-page">
      <Seo title={t("upg.seo")} noindex />
      <Link to="/app/coach" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="upg-back">
        <ChevronLeft className="h-4 w-4" /> {t("cset.back_dashboard")}
      </Link>
      <PageHeader eyebrow={t("upg.eyebrow")} title={t("upg.title")} subtitle={t("upg.subtitle")} />

      {isPro && (
        <div className="mb-5 flex items-center gap-3 rounded-2xl border border-emerald-300 bg-emerald-50 p-4" data-testid="upg-current">
          <Crown className="h-5 w-5 shrink-0 text-emerald-600" />
          <p className="text-sm font-medium text-emerald-800">{t("upg.already_pro")}</p>
        </div>
      )}

      {tiers === null ? (
        <div className="grid gap-4 sm:grid-cols-2">{[0, 1].map((i) => <Skeleton key={i} className="h-80 rounded-2xl" />)}</div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {PLANS.map((key) => {
            const tier = tiers[key];
            if (!tier) return null;
            const yearly = key.endsWith("_yearly");
            const isCurrent = currentTier === key;
            return (
              <div key={key} data-testid={`upg-plan-${key}`} className={`relative flex flex-col rounded-3xl border p-6 ${yearly ? "border-primary bg-primary/5" : "border-border bg-card"}`}>
                {yearly && <span className="absolute -top-2.5 right-5 rounded-full bg-primary px-3 py-0.5 text-[10px] font-semibold text-primary-foreground">{t("upg.best_value")}</span>}
                <p className="flex items-center gap-1.5 font-display text-xl text-foreground"><Sparkles className="h-4 w-4 text-primary" /> {t(yearly ? "upg.plan_yearly" : "upg.plan_monthly")}</p>
                <p className="mt-2">
                  <span className="font-display text-4xl text-foreground">{eur(tier.price_eur_cents)}</span>
                  <span className="text-sm text-muted-foreground">/{t(yearly ? "upg.per_year" : "upg.per_month")}</span>
                </p>
                <ul className="mt-4 flex-1 space-y-2">
                  {FEATURE_KEYS.map((fk) => (
                    <li key={fk} className="flex items-start gap-2 text-sm text-foreground">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" /> {t(`upg.${fk}`)}
                    </li>
                  ))}
                </ul>
                <div className="mt-5 space-y-2">
                  <Button className="w-full rounded-full" disabled={isCurrent || Boolean(busy)} onClick={() => subscribe(key, "stripe")} data-testid={`upg-stripe-${key}`}>
                    {isCurrent ? t("upg.current") : (busy === `${key}-stripe` ? t("common.loading") : t("upg.subscribe"))}
                  </Button>
                  {!isCurrent && (
                    <Button variant="outline" className="w-full rounded-full" disabled={Boolean(busy)} onClick={() => subscribe(key, "credits")} data-testid={`upg-credits-${key}`}>
                      {busy === `${key}-credits` ? t("common.loading") : t("upg.pay_credits", { n: tier.price_credits })}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      <p className="mt-4 text-center text-xs text-muted-foreground">{t("upg.vat_note")}</p>
    </div>
  );
}
